<template>
	<div class="url-builder generic-form" v-cloak v-if="config">

		<!-- Source URL -->
        <url-source
            :title="__('label_url_domain')"
            :placeholder="__('placeholder_url_domain')"
            :url.sync="sourceUrl"
        ></url-source>

		<builder-row v-if="url?.length == 0 | !isUrlValid()">
			<p class="text-base mt-4" v-html="__('instructions_invalid_url')"></p>
		</builder-row>

		<div v-show="isUrlValid()">

			<!-- All URL parameters -->
			<div class="url-parameters" ref="urlParameters" v-show="urlParameters.length > 0">
				<transition 
					name="builder-row" 
					mode="in-out"
					:key="parameter.id"
					v-for="parameter in urlParameters"
					appear
				>
					<url-parameter
						:parameterKey.sync="parameter.parameterKey"
						:parameterValue.sync="parameter.parameterValue"
						:placeholderKey="__('placeholder_key')"
						:placeholderValue="parameter.placeholderValue ?? __('placeholder_value')"
						:hideKey="parameter.hideKey"
						:label="parameter.label"
						@remove="removeParameter(parameter)"
						:removeable="parameter.removeable ?? false"
					></url-parameter>
				</transition>
			</div>

			<!-- Anchor input -->
			<transition 
				name="builder-row" 
				mode="in-out"
				appear
			>
				<builder-row class="mt-8 lg:mt-8" v-show="isAnchorVisible" :label="__('label_anchor')" @remove="removeAnchor()" removeable>
					<div class="anchor">
						<div class="hashtag">#</div>
						<input type="text" :placeholder="__('placeholder_anchor')" v-model="anchor" ref="anchorInput">
					</div>
				</builder-row>
			</transition>

			<!-- Button to add parameters -->
			<builder-row class="mt-6">
				<a class="button-underline select-none" @click="addParameter" href="javascript:;">
					<span class="text-2xl">+</span>
					<span>{{ __('button_add_parameter') }}</span>
				</a>
			</builder-row>

			<!-- Button to show the anchor input -->
			<builder-row class="mt-3" v-show="!isAnchorVisible">
				<a class="button-underline select-none" @click="showAnchor()" href="javascript:;">
					<span class="text-2xl">+</span>
					<span>{{ __('button_add_anchor') }}</span>
				</a>
			</builder-row>

			<!-- URL result -->
			<div class="mt-12">	
				<h3 class="text-lg mb-1">{{ __('label_result') }}</h3>
				<url-result :value="url" :placeholder="__('placeholder_domain')"></url-result>
			</div>

		</div>

	</div>

</template>

<script>
import uuid from '../helpers/uuid';
import BuilderRow from './BuilderRow.vue';

export default {
	components: { BuilderRow },
	props: {
		siteUrl: String,
		templateParameters: String
	},
	mounted() {
		this.loadConfig();
		this.urlParameters = JSON.parse(this.templateParameters).map((parameter) => {
			parameter.parameterKey = parameter.parameter_key;
			parameter.placeholderValue = parameter.value_placeholder;
			parameter.hideKey = true;
			parameter.removeable = false;
			return parameter;
		});
	},
	data() {
		return {
            anchor: '',
			config: null,
            isAnchorVisible: false,
            sourceUrl: null,
			urlParameters: []
		};
	},
    computed: {
        url() {
            try {
				let sourceUrl = this.sourceUrl;
				if (!sourceUrl.startsWith('https://') & !sourceUrl.startsWith('http://'))
					sourceUrl = 'https://' + sourceUrl;

                const url = new URL(sourceUrl);
                url.search = '';

                this.urlParameters.forEach(p => {
                    if (p.parameterKey)
                        url.searchParams.append(p.parameterKey ?? '', p.parameterValue ?? '');
                });

                url.hash = this.anchor;

                return url.href;
            } catch (e) {
                return null;
            }
        }
    },
    methods: {
        async addParameter() {
            this.urlParameters.push({
                id: uuid(),
				label: this.__('label_parameter'),
				removeable: true
            });

            await this.$nextTick();

            const lastParameterKey = this.$refs.urlParameters.querySelector('.builder-row:last-child .parameterKey');
            if (lastParameterKey) 
				lastParameterKey.focus();
        },
		async loadConfig() {
			let locale = this.siteUrl.replace(/\/$/, ''); // remove trailing slash
			fetch(`${locale}/url-builder/config`)
				.then(response => response.json())
				.then(data => (this.config = data));
		},
        async showAnchor() {
            this.isAnchorVisible = true;
            await this.$nextTick();
            this.$refs.anchorInput.focus();
        },
		isUrlValid() {
			var urlPattern = new RegExp('^(https?:\\/\\/)?'+ 			// validate protocol
				'((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|'+ 	// validate domain name
				'((\\d{1,3}\\.){3}\\d{1,3}))'+ 							// validate OR ip (v4) address
				'(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*'+ 						// validate port and path
				'(\\?[;&a-z\\d%_.~+=-]*)?'+ 							// validate query string
				'(\\#[-a-z\\d_]*)?$','i'); 								// validate fragment locator
			return !!urlPattern.test(this.sourceUrl);
		},
		removeParameter(parameter) {
            this.urlParameters = this.urlParameters.filter((p) => {
                return p.id !== parameter.id;
            });
        },
		removeAnchor() {
            this.isAnchorVisible = false;
			this.anchor = '';
        },
		translation(key) {
			if (!this.config?.translations)
				return key;
			return this.config.translations[key];
		},
		__(key) {
			return this.translation(key);
		}
    }
};
</script>
